/**
 * Resolver for the unified "Tonelaje-Ley-Metal" config: from the user's
 * selections it derives which real Lib algorithm to invoke and whether the run
 * has to fan out into one call per category. Kept pure (no store/UI) so the
 * derivation table is testable on its own and has a single home.
 *
 * The catalog surfaces ONE entry; the two Lib algorithms below are an internal
 * detail the user never picks directly.
 */

import { getAlgorithmSchema, inheritedParameters } from '../config/algorithms';
import type { CombinedFilter, FilterSpec } from '../types/compute';
import { numericCategoricalToContinuous, periodOrFilterId, withAutoCombined } from './filterSpec';
import type { AlgorithmInput } from './batchRequestBuilder';

export const TONNAGE_BY_CUTOFF = 'tonnage_grade_metal_by_cutoff';
export const TONNAGE_BY_CATEGORIES = 'tonnage_grade_metal_by_categories';

export type TonnageLibName = typeof TONNAGE_BY_CUTOFF | typeof TONNAGE_BY_CATEGORIES;

/** The selections that decide the algorithm + plot (measures affect only the
 *  plot shape downstream, so they don't enter here). */
export interface TonnageModeInput {
  /** How many cut-off grades the user entered. */
  cutoffCount: number;
  /** Whether the user asked to group by a geological category. */
  groupByCategory: boolean;
}

export interface TonnageMode {
  /** The real Lib algorithm to invoke. */
  libName: TonnageLibName;
  /**
   * Scenario 3: a multi cut-off run grouped by category can't run in a single
   * Lib call, so it fans out into one `by_cutoff` call per category value (each
   * with a categorical filter). `false` for the single-call cases.
   */
  perCategory: boolean;
}

/**
 * The three ways the unified "Tonelaje-Ley-Metal" entry compares models — the
 * user picks one, and it maps to a Lib algorithm (+ build-time fan-out):
 *   • `cutoff`           → by_cutoff over the cut-off list (X = cut-off).
 *   • `category`         → by_categories over the categories (X = category),
 *                          one cut-off.
 *   • `categoryByCutoff` → scenario 3: by_cutoff fanned out into one call per
 *                          category (each filtered), so every category gets its
 *                          own cut-off curve. Stored as a by_cutoff entry whose
 *                          `unique_categories` (UI-only) lists the categories to
 *                          split by.
 */
export type TonnageUiMode = 'cutoff' | 'category' | 'categoryByCutoff';

/** Read the current mode off a stored entry. `by_cutoff` carrying a
 *  `unique_categories` key is the scenario-3 marker (that param isn't part of the
 *  by_cutoff Lib schema — it's the UI's split list, stripped at build). */
export function currentTonnageMode(algo: AlgorithmInput): TonnageUiMode {
  if (algo.name === TONNAGE_BY_CATEGORIES) return 'category';
  return 'unique_categories' in algo.parameters ? 'categoryByCutoff' : 'cutoff';
}

/**
 * Switch a tonnage entry to a mode, preserving as much input as possible.
 * `inheritedParameters` seeds the target algorithm's defaults and carries over
 * every shared param with its current value; the cut-off is bridged by hand
 * (`cutoffs` list ↔ `cutoff` scalar) so a typed value isn't lost; and the
 * per-mode stash refills only what the target mode alone owns. `models`,
 * `plotMeasures` and `criterioSeleccion` are untouched. No-op when already in
 * the mode.
 */
export function setTonnageMode(algo: AlgorithmInput, mode: TonnageUiMode): AlgorithmInput {
  const from = currentTonnageMode(algo);
  if (from === mode) return algo;

  const targetName = mode === 'category' ? TONNAGE_BY_CATEGORIES : TONNAGE_BY_CUTOFF;
  // Stash the outgoing mode's params and restore any earlier stash for the
  // target (US 2079): switching away and back must not cost the user their
  // typed config. The stash lives OUTSIDE parameters — `unique_categories`
  // inside a by_cutoff's parameters is the categoryByCutoff marker
  // (currentTonnageMode), so parking category params there would misdetect
  // the mode. Cloned in and out so a stashed array is never the same object
  // as the live one (a later in-place edit would rewrite history).
  const modeStash = { ...(algo.modeStash ?? {}), [from]: structuredClone(algo.parameters) };
  const restored = algo.modeStash?.[mode] ?? {};
  const parameters = inheritedParameters(targetName, algo.parameters);

  // Bridge the cut-off, whose shape differs by algorithm (scalar vs list).
  const cutoffs = algo.parameters.cutoffs;
  const cutoff = algo.parameters.cutoff;
  const asList = Array.isArray(cutoffs) ? cutoffs : typeof cutoff === 'number' ? [cutoff] : [];
  if (mode === 'category') {
    if (typeof asList[0] === 'number') parameters.cutoff = asList[0];
  } else if (asList.length > 0) {
    parameters.cutoffs = asList;
  }

  // Restore ONLY what inheritance couldn't carry: the target mode's exclusive
  // params (the cut-off list/scalar, the category selection). The two schemas
  // share 11 params and `inheritedParameters` already seeded those with their
  // CURRENT value, so assigning the whole stash on top would revert the user's
  // latest edit to the snapshot taken when they last left this mode — the same
  // loss this US fixes, mirrored. Testing `!(k in parameters)` would not work:
  // `defaultParameters` seeds every target param, so `cutoffs` is always
  // present. The exclusive keys are exactly those the source lacks. Restored
  // values still overwrite the cut-off bridge above — the bridge only salvages
  // a value when there is nothing to restore.
  const carried = new Set(
    (getAlgorithmSchema(targetName)?.params ?? [])
      .filter((p) => algo.parameters[p.name] !== undefined)
      .map((p) => p.name)
  );
  for (const [k, v] of Object.entries(restored)) {
    if (!carried.has(k)) parameters[k] = structuredClone(v);
  }

  // The scenario-3 marker: a UI-only split list on a by_cutoff entry — and ONLY
  // there. A plain by_cutoff carrying it (e.g. restored from a category stash)
  // would misread as categoryByCutoff, so it is stripped on the way in.
  if (mode === 'categoryByCutoff') {
    parameters.unique_categories = Array.isArray(parameters.unique_categories)
      ? parameters.unique_categories
      : Array.isArray(algo.parameters.unique_categories)
        ? algo.parameters.unique_categories
        : [];
  } else if (mode === 'cutoff') {
    delete parameters.unique_categories;
  }
  return { ...algo, name: targetName, parameters, modeStash };
}

/** The categories this entry splits by; empty when it is not a category split. */
function splitCategories(a: AlgorithmInput): string[] {
  if (a.name !== TONNAGE_BY_CUTOFF || !('unique_categories' in a.parameters)) return [];
  return Array.isArray(a.parameters.unique_categories)
    ? (a.parameters.unique_categories as string[]).filter((c): c is string => typeof c === 'string')
    : [];
}

/**
 * What deleting ONE figure of a configured chart should do to the configuration.
 *
 * Three outcomes, named rather than encoded in a nullable return, because they are
 * genuinely different acts and the caller must not confuse them:
 *
 *   `entry`      — drop the whole configured chart. Either it owns a single figure,
 *                  or this was the last category of a split.
 *   `category`   — the chart survives with one category fewer. Its other figures
 *                  keep their chart ids (the `-N` suffix counts config positions,
 *                  not draw order), so their palette and captions stay put.
 *   `figureOnly` — remove the figure from view and leave the configuration alone.
 *                  The figure's category is not one the card lists any more, so the
 *                  user already edited them and there is nothing here to unpick.
 *
 * `category` is the figure's own category — see `chartCategoryMap` in
 * lib/resultToDashboard. Named rather than numbered on purpose: any index into the
 * list moves when a sibling is unpicked, whether it counts drawn envelopes or
 * positions in the config. Before this, every figure of a split resolved to the
 * card's one id, so deleting any of them dropped the card and pruned them all
 * (Bug 2275).
 */
export type SplitDeletion =
  | { kind: 'entry' }
  | { kind: 'category'; entry: AlgorithmInput }
  | { kind: 'figureOnly' };

export function splitDeletionFor(a: AlgorithmInput, category: string | null): SplitDeletion {
  const categories = splitCategories(a);
  // Not a split, or a figure we cannot name: one configured chart, one figure.
  if (categories.length === 0 || category === null) return { kind: 'entry' };
  // Matched loosely, removed exactly. A numeric-looking category came back from the
  // lowered filter as a plain number ("1.0" reads as "1"), so a strict compare said
  // the card did not list it and the delete became a silent no-op: the figure left
  // the screen, the configuration kept it, and the next run drew it again.
  // By POSITION, not by value: the list can hold the same label twice. Reopening a
  // reconciliation rebuilds it from the lowered filters, and two labels that select
  // the same blocks ("1.0" and "1", offered together when LP and CP spell the
  // category differently) both read back as "1". Removing every entry equal to the
  // one matched took the card's other category too — one delete, both figures gone,
  // which is the shape of Bug 2275 all over again. The index is resolved here,
  // against the list as it stands, so nothing depends on it staying put.
  const at = categories.findIndex((c) => sameCategoryLabel(c, category));
  // Checked BEFORE the last-category case: a card can still own an on-screen figure
  // from a run whose category it no longer lists, and dropping the card would take
  // the categories the user kept.
  if (at < 0) return { kind: 'figureOnly' };
  if (categories.length === 1) return { kind: 'entry' };
  return {
    kind: 'category',
    entry: {
      ...a,
      parameters: {
        ...a.parameters,
        unique_categories: categories.filter((_, i) => i !== at),
      },
    },
  };
}

/**
 * Whether two labels name the same category as far as the data is concerned.
 *
 * The reader stores a numeric-looking cell as a double, so "1.0", "01" and "1" all
 * select the same blocks — and the lowering leans on that, turning such a category
 * into a continuous filter pinned to [n, n] (numericCategoricalToContinuous). Read
 * back, the label is the number, not the string the user picked from.
 */
function sameCategoryLabel(a: string, b: string): boolean {
  if (a === b) return true;
  if (a.trim() === '' || b.trim() === '') return false;
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
}

/**
 * The id shape the lowering gives a category-split filter: `m{model}cat{index}`.
 * Its own namespace, distinct from the ids a user's filter card gets
 * (`m{model}f{n}`, filterSpec's nextFilterId), which is what makes recognising the
 * marker on the way back a lookup rather than a guess about user data.
 */
const SPLIT_FILTER_ID = /^m\d+cat\d+$/;

/**
 * The shape `withSplitFilter` leaves on a user-authored selector: their whole
 * expression parenthesised, ANDed with a marker id (the last token). Recognising it
 * is what makes the un-wrap on the way back exact rather than a re-parse of the
 * boolean.
 */
const MARKER_CONJUNCT = /^\((.*)\)\s+AND\s+([A-Za-z0-9_]+)$/;

/**
 * The category a split filter stands for, or null when it is not one.
 *
 * The ONE place that reads a category back out of a lowered entry, used from both
 * shapes: the draft's per-model `filters` (collapsing on load) and the wire's flat
 * `filters` (naming a figure while the dashboard is built).
 */
function categoryOfSplitFilters(filters: readonly FilterSpec[]): string | null {
  for (const f of filters) {
    if (!SPLIT_FILTER_ID.test(f.id)) continue;
    if (f.type === 'categorical') return f.category_value;
    // A numeric-looking category cannot run as categorical, so the lowering pinned
    // it to a continuous [n, n] under the SAME id (numericCategoricalToContinuous).
    // Reading the number back is exact for the filter; the LABEL can differ where
    // the original string was not canonical ("1.0" returns as "1"), which changes
    // nothing about which blocks are selected.
    if (f.type === 'continuous' && f.min_limit_value === f.max_limit_value) {
      return String(f.min_limit_value);
    }
  }
  return null;
}

/** Draft shape: the category a lowered entry filters on, across its models. */
function loweredSplitCategory(a: AlgorithmInput): string | null {
  if (a.name !== TONNAGE_BY_CUTOFF) return null;
  for (const m of a.models) {
    const found = categoryOfSplitFilters(m.filters ?? []);
    if (found !== null) return found;
  }
  return null;
}

/**
 * Wire shape: the category ONE executed analysis was filtered to, or null.
 *
 * This is what names a figure of a split. The chart id carries it rather than an
 * ordinal, because an ordinal moves: unpick the middle category and the third
 * figure inherits the second's number, and with it the palette and the caption the
 * user saved for the second — presentation re-attaches by chart id. The category is
 * what the figure IS, so it does not move when a sibling leaves.
 */
export function executedSplitCategory(entry: {
  name: string;
  filters?: FilterSpec[];
}): string | null {
  if (entry.name !== TONNAGE_BY_CUTOFF) return null;
  return categoryOfSplitFilters(entry.filters ?? []);
}

/** The entry with the split marker removed and each model's auto-combined re-synced. */
function withoutSplitFilter(a: AlgorithmInput): AlgorithmInput {
  return {
    ...a,
    models: a.models.map((m, mi) => {
      const present = m.filters ?? [];
      const kept = present.filter((f) => !SPLIT_FILTER_ID.test(f.id)).map(withoutSplitConjunct);
      if (kept.length === present.length) return m;
      // Re-run the auto-combined: the expression it was given references the marker
      // we just dropped, and with one leaf left it should not exist at all. An empty
      // list stays an empty list rather than removing the key — buildBatchRequest
      // gates on `length > 0`, so the two lower identically and the round trip holds.
      // A selector the user wrote is not auto-maintained, so `withoutSplitConjunct`
      // above is what returns it to the expression they typed.
      return { ...m, filters: withAutoCombined(kept, mi) };
    }),
  };
}

/**
 * Inverse of {@link expandTonnageByCategory}: fold a lowered fan-out back into the
 * one configured chart it came from.
 *
 * The lowering strips `unique_categories` — it is not a by_cutoff Lib param — so
 * without this a saved reconciliation reopened as N plain cut-off entries, each
 * carrying a categorical filter it never asked for. The card lost its categories
 * control, and every figure still resolved to the FIRST of the N, so deleting any of
 * them pruned all N and its ⚙ read the first category's parameters (Bug 2275). The
 * in-session fix does not reach that state, because the state only exists after a
 * round trip.
 *
 * Runs BEFORE `uniqueInstanceIds` (batchRequestToDraft), which would otherwise see N
 * entries sharing an id and split them into N independent charts — the very shape
 * this undoes. Grouping is by shared identity: entries that predate minting have no
 * id to group by and pass through as they are, which is correct for data that is
 * disposable anyway.
 */
export function collapseTonnageByCategory(entries: AlgorithmInput[]): AlgorithmInput[] {
  const out: AlgorithmInput[] = [];
  let i = 0;
  while (i < entries.length) {
    const first = entries[i];
    const category = loweredSplitCategory(first);
    if (category === null || !first.instanceId) {
      out.push(first);
      i += 1;
      continue;
    }
    // Only the categories survive the fold — `first` becomes the one card — so an
    // entry that differs in anything ELSE must not join the group, or the difference
    // is lost without a word and the next run computes something the stored config
    // never asked for. The fan-out emits siblings identical but for their marker, so
    // this costs nothing on data the picker produced; it is the hand-edited and the
    // migrated config that this refuses to flatten.
    const base = withoutSplitFilter(first);
    const shape = canonical(base);
    const categories = [category];
    let j = i + 1;
    while (j < entries.length && entries[j].instanceId === first.instanceId) {
      const next = loweredSplitCategory(entries[j]);
      if (next === null || canonical(withoutSplitFilter(entries[j])) !== shape) break;
      categories.push(next);
      j += 1;
    }
    out.push({ ...base, parameters: { ...base.parameters, unique_categories: categories } });
    i = j;
  }
  return out;
}

/**
 * JSON with object keys sorted, so two entries that describe the same computation
 * compare equal whatever order their keys were serialized in — the same normalising
 * `instanceStaleness` does before it compares two lowered entries.
 */
function canonical(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`)
    .join(',')}}`;
}

/**
 * Build-time fan-out for scenario 3: a by_cutoff entry carrying a
 * `unique_categories` split list becomes one by_cutoff invocation per category,
 * each with a categorical filter (on that model's category column) ANDed into the
 * model's selector so the call really runs filtered (see {@link withSplitFilter}).
 * The UI-only `unique_categories` is stripped from the
 * emitted params (it isn't a by_cutoff Lib param). Every other entry passes
 * through unchanged. An empty split list emits a single unfiltered call.
 */
export function expandTonnageByCategory(a: AlgorithmInput): AlgorithmInput[] {
  if (a.name !== TONNAGE_BY_CUTOFF || !('unique_categories' in a.parameters)) return [a];
  const values = Array.isArray(a.parameters.unique_categories)
    ? (a.parameters.unique_categories as string[])
    : [];
  const parameters = { ...a.parameters };
  delete parameters.unique_categories;
  if (values.length === 0) return [{ ...a, parameters }];

  return values.map((value, ci) => ({
    ...a,
    parameters,
    models: a.models.map((m, mi) => {
      const colPos =
        typeof a.parameters[`category_column_index_${mi + 1}`] === 'number'
          ? (a.parameters[`category_column_index_${mi + 1}`] as number)
          : 0;
      return {
        ...m,
        filters: withSplitFilter(
          m.filters ?? [],
          categorySplitFilter(`m${mi}cat${ci}`, colPos, value),
          mi
        ),
      };
    }),
  }));
}

/**
 * Add the split marker to ONE model's filters so the call actually runs filtered.
 *
 * The selector is what decides: `model_filters` points at a model's `combined`
 * filter, and the C++ side evaluates that expression — a filter card the expression
 * never names is inert, however present it is in the pool. So the marker cannot be
 * appended and left to `withAutoCombined`, which deliberately backs off the moment
 * the expression stops being a plain AND chain: the user wrote it, and it is theirs.
 * With a hand-written selector ("m0f1 OR m0f2") the fan-out used to emit N calls
 * that all ran UNFILTERED — the same numbers drawn N times under N category names —
 * and on the way back `liftModelFilters` did not lift a card the selector never
 * named, so the entries stopped looking like a split and reopened as N plain
 * cut-off cards (Bug 2275). Theirs to write, ours to conjoin: the expression is
 * kept whole, in parentheses, ANDed with the marker.
 */
function withSplitFilter(present: FilterSpec[], split: FilterSpec, mi: number): FilterSpec[] {
  const auto = withAutoCombined([...present, split], mi);
  // THE SELECTOR — the card `model_filters` will point at — chosen the way
  // lowerModelFilters chooses it: the periods-of-interest OR-group (US 2203) is a
  // combined too, but it is nested INSIDE the model's top-level AND rather than
  // being the selector, unless it is the only combined there is. Conjoining the
  // marker onto the OR-group instead would rewrite an expression that is wholly
  // derived and regenerated on every edit.
  const orId = periodOrFilterId(mi);
  const combined =
    auto.find((f): f is CombinedFilter => f.type === 'combined' && f.id !== orId) ??
    auto.find((f): f is CombinedFilter => f.type === 'combined');
  // Either there is no selector at all — the marker is the model's only card and the
  // slot points straight at it — or the auto-maintained chain already names it. The
  // scan is by whole token, the way `referencedIds` reads an expression.
  const names = combined?.expression.split(/[^A-Za-z0-9_]+/).includes(split.id);
  if (!combined || names) return auto;
  // It backed off, so the expression is the user's. Theirs to write, ours to
  // conjoin: keep it whole, in parentheses, ANDed with the marker — and last in the
  // list, the leaves-first order the auto-combined also returns.
  return [
    ...auto.filter((f) => f !== combined),
    { ...combined, expression: `(${combined.expression}) AND ${split.id}` },
  ];
}

/** Undo what {@link withSplitFilter} did to a user-authored selector, for the
 *  marker ids `marker` matches. Anything else — a plain AND chain, a leaf — passes
 *  through: `withAutoCombined` re-syncs the chains it owns, and leaves have no
 *  expression to unpick. Shared with the `error` cut-off (lib/errorCutoff). */
export function withoutMarkerConjunct(f: FilterSpec, marker: RegExp): FilterSpec {
  if (f.type !== 'combined') return f;
  const inner = MARKER_CONJUNCT.exec(f.expression);
  return inner && marker.test(inner[2]) ? { ...f, expression: inner[1] } : f;
}

function withoutSplitConjunct(f: FilterSpec): FilterSpec {
  return withoutMarkerConjunct(f, SPLIT_FILTER_ID);
}

/** The per-category filter for the scenario-3 fan-out: a categorical filter,
 *  lowered to continuous [n, n] when the category looks numeric (the reader
 *  stores those cells as double — see numericCategoricalToContinuous, which the
 *  builder also applies to user-authored filter cards). */
function categorySplitFilter(id: string, column: number, value: string): FilterSpec {
  return numericCategoricalToContinuous({
    id,
    type: 'categorical',
    category_column: column,
    category_value: value,
  });
}

export function resolveTonnageMode({
  cutoffCount,
  groupByCategory,
}: TonnageModeInput): TonnageMode {
  const multiCutoff = cutoffCount > 1;

  // Grouped by category with a single cut-off → the by_categories algorithm
  // handles it in one call (x axis = categories).
  if (groupByCategory && !multiCutoff) {
    return { libName: TONNAGE_BY_CATEGORIES, perCategory: false };
  }

  // Grouped by category with multiple cut-offs → no single Lib algorithm covers
  // the cut-off × category matrix, so fan out into one by_cutoff call per
  // category (scenario 3).
  if (groupByCategory && multiCutoff) {
    return { libName: TONNAGE_BY_CUTOFF, perCategory: true };
  }

  // No category → by_cutoff, one call (single or multi cut-off).
  return { libName: TONNAGE_BY_CUTOFF, perCategory: false };
}
